import { Check, ChevronDown } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { ConversationBrand } from "@/lib/api";
import { cn } from "@/lib/utils";

import { CONVERSATION_BRANDS } from "./brand";
import { BrandBadge } from "./brand-badge";

interface BrandSwitcherProps {
  /** The brand currently in context (conversation's brand, or `?brand=`). */
  brand: string;
  /**
   * Picking the other brand. The chat page navigates to `/chat?brand=…`, which
   * deliberately drops the open conversation: a conversation's brand is
   * immutable server-side, so "switching" means browsing the other brand's
   * catalog from scratch.
   */
  onSelect: (brand: ConversationBrand) => void;
}

/**
 * Header control showing which brand catalog the chat is scoped to, with the
 * other brand one click away. Replaces the static header badge so the brand is
 * both visible and actionable, including on bare `/chat` where no conversation
 * pins it.
 */
export function BrandSwitcher({ brand, onSelect }: BrandSwitcherProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Brand: ${brand}. Switch brand`}
          className={cn(
            "flex shrink-0 cursor-pointer items-center gap-1 rounded-full outline-none",
            "focus-visible:ring-ring hover:opacity-80 focus-visible:ring-2",
          )}
        >
          <BrandBadge brand={brand} />
          <ChevronDown className="text-muted h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="border-border bg-card text-foreground w-52">
        <DropdownMenuLabel className="text-muted text-xs font-semibold">
          Brand catalog
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-border" />
        {CONVERSATION_BRANDS.map((option) => {
          const isCurrent = option === brand;
          return (
            <DropdownMenuItem
              key={option}
              onSelect={() => onSelect(option)}
              className="focus:bg-accent-light focus:text-primary cursor-pointer justify-between"
            >
              <span className={cn("truncate", isCurrent && "text-primary font-medium")}>
                {option}
              </span>
              {isCurrent && <Check className="text-primary h-4 w-4" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
